/**
 * API Client for NurinV2
 *
 * Uses fetch with proper error handling and type-safe responses.
 * Vite proxy routes /api/* to http://localhost:3001 in development.
 *
 * @see https://hono.dev/docs/guides/rpc
 */

const API_BASE = import.meta.env.VITE_API_URL ? `${import.meta.env.VITE_API_URL}/api/v1` : '/api/v1'

/** Error thrown by `apiFetch` — carries the API's `code` (e.g. `WINDOW_EXPIRED`) when present. */
export interface ApiError extends Error {
  code?: string
  windowInfo?: { hoursRemaining?: number; expiresAt?: string | null }
}

// In-memory token store — fallback when cross-origin cookies are blocked
let _accessToken: string | null = null

export function setAccessToken(token: string | null) {
  _accessToken = token
}

/**
 * Generic fetch wrapper with error handling
 */
async function apiFetch<T>(endpoint: string, options?: RequestInit): Promise<T> {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...(options?.headers as Record<string, string>),
  }

  // Send Bearer token as fallback when cookies don't work cross-origin
  if (_accessToken && !headers['Authorization']) {
    headers['Authorization'] = `Bearer ${_accessToken}`
  }

  const res = await fetch(`${API_BASE}${endpoint}`, {
    ...options,
    credentials: 'include',
    headers,
  })

  if (!res.ok) {
    const error = await res.json().catch(() => ({ error: 'Unknown error' }))
    const err = new Error(error.error || error.message || `HTTP ${res.status}`) as ApiError
    if (error.code) err.code = error.code
    if (error.windowInfo) err.windowInfo = error.windowInfo
    throw err
  }

  return res.json()
}

// ============================================================================
// Auth Types
// ============================================================================
export type UserRole =
  | 'super_admin'
  | 'admin'
  | 'doctor'
  | 'branch_manager'
  | 'clinic_staff'
  | 'marketing'
  | 'analyst'

export interface AuthUser {
  id: number
  username: string
  name: string
  role: UserRole
  avatarUrl: string | null
  phone: string | null
  mustChangePassword: boolean
  hasAllBranchAccess: boolean
  allowedBranchIds: number[]
}

export interface LoginRequest {
  username: string
  password: string
}

export interface LoginResponse {
  success: boolean
  user: AuthUser
  accessToken: string
}

export interface AuthMeResponse {
  user: AuthUser
}

export interface AuthCheckResponse {
  authenticated: boolean
  userId?: number
  role?: string
}

export interface ChangePasswordRequest {
  currentPassword: string
  newPassword: string
  confirmPassword: string
}

export interface ResetPasswordRequest {
  userId: number
}

// ============================================================================
// Auth API
// ============================================================================
export const authApi = {
  login: (data: LoginRequest) =>
    apiFetch<LoginResponse>('/auth/login', {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  logout: () =>
    apiFetch<{ success: boolean }>('/auth/logout', {
      method: 'POST',
    }),

  me: () => apiFetch<AuthMeResponse>('/auth/me'),

  check: () => apiFetch<AuthCheckResponse>('/auth/check'),

  refresh: () =>
    apiFetch<{ success: boolean; accessToken: string }>('/auth/refresh', {
      method: 'POST',
    }),

  changePassword: (data: ChangePasswordRequest) =>
    apiFetch<{ success: boolean; message: string }>('/auth/change-password', {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  resetPassword: (data: ResetPasswordRequest) =>
    apiFetch<{ success: boolean; message: string }>('/auth/reset-password', {
      method: 'POST',
      body: JSON.stringify(data),
    }),
}

// ============================================================================
// Patient Types
// ============================================================================
export interface Patient {
  id: number
  branchId: number
  branchCode: string | null
  branchName: string | null
  pno: string
  nric: string | null
  name: string
  gender: string | null
  dateOfBirth: string | null
  age: number | null
  phone: string | null
  email: string | null
  address?: string | null
  nationality?: string | null
  ethnicity?: string | null
  insurance?: string | null
  referralSource?: string | null
  totalVisits: number | null
  totalAppointments?: number | null
  totalSpent: string | null
  registrationDate: string | null
  lastVisitDate: string | null
  createdAt: string
  updatedAt?: string
  // WhatsApp campaign history (list/export endpoints only)
  waHasContact?: boolean
  waBlastCount?: number
  waLastSentAt?: string | null
  waLastStatus?: 'pending' | 'sent' | 'delivered' | 'read' | 'failed' | null
  waLastReplied?: boolean
  waLastCampaignName?: string | null
  waEverReplied?: boolean
  waOptedOut?: boolean
  // Clinic blacklist (detail endpoint only). Blacklisted patients are excluded
  // from follow-up queues and WhatsApp campaign audiences.
  blacklistedAt?: string | null
  blacklistedBy?: number | null
  blacklistedByName?: string | null
  blacklistReason?: string | null
}

export interface PatientBlacklistInput {
  blacklisted: boolean
  /** Required when `blacklisted` is true. */
  reason?: string
}

export interface PatientBlacklistResponse {
  data: {
    id: number
    blacklistedAt: string | null
    blacklistedBy: number | null
    blacklistedByName: string | null
    blacklistReason: string | null
  }
}

export type WaFilter =
  | 'any'
  | 'never'
  | 'last30d'
  | 'noReply'
  | 'replied'
  | 'optedOut'
  | 'noContact'

export type PatientsWaSummary = Record<WaFilter, number>

export interface PatientStats {
  totalInvoices: number
  totalRevenue: number
  avgPerVisit: number
  firstVisit: string | null
  lastVisit: string | null
}

export interface PatientInvoice {
  id: number
  invoiceNo: string
  invoiceDate: string
  grandTotal: string
  status: string | null
  patientType: string | null
  paymentMethod: string | null
  createdBy?: string | null
}

export type LastVisitFilter =
  | 'all'
  | 'active3m'
  | 'active6m'
  | 'active12m'
  | 'inactive3m'
  | 'inactive6m'
  | 'inactive12m'

export interface PatientsListParams {
  branchId?: number
  search?: string
  page?: number
  pageSize?: number
  sortBy?:
    | 'name'
    | 'pno'
    | 'totalVisits'
    | 'totalSpent'
    | 'lastVisitDate'
    | 'createdAt'
    | 'waLastSentAt'
  sortOrder?: 'asc' | 'desc'
  dateFilter?: 'today' | 'week' | 'month' | 'lastMonth' | 'last3Months' | 'year'
  lastVisitFilter?: LastVisitFilter
  hasPhone?: boolean
  waFilter?: WaFilter
}

export interface PatientsExportParams {
  branchId?: number
  lastVisitFilter?: LastVisitFilter
  hasPhone?: boolean
  format?: 'basic' | 'full'
  search?: string
  waFilter?: WaFilter
}

export interface PatientsWaSummaryParams {
  branchId?: number
  lastVisitFilter?: LastVisitFilter
  search?: string
}

export interface PatientsExportResponse {
  data: Patient[]
  stats: {
    total: number
    withPhone: number
    withoutPhone: number
  }
  format: 'basic' | 'full'
}

export interface PatientsListResponse {
  data: Patient[]
  total: number
  page: number
  pageSize: number
  pageCount: number
}

export interface PatientDetailResponse {
  data: Patient
  stats: PatientStats
  recentInvoices: PatientInvoice[]
}

export interface PatientInvoicesResponse {
  data: PatientInvoice[]
  total: number
  page: number
  pageSize: number
  pageCount: number
}

export interface UpdatePatientInput {
  name?: string
  nric?: string | null
  phone?: string | null
  email?: string | null
  gender?: 'Male' | 'Female' | null
  dateOfBirth?: string | null
  address?: string | null
  nationality?: string | null
  ethnicity?: string | null
  insurance?: string | null
  referralSource?: string | null
}

// ============================================================================
// Stock (balancing) Types
// ============================================================================
export interface StockOverviewParams {
  from?: string
  to?: string
  type?: 'product' | 'medication' | 'all'
  branchId?: number
  minRatio?: number
  coverageDays?: number
  months?: number
}
export interface StockVelocityRow {
  skuId: number
  sku: string
  type: 'product' | 'medication'
  branchId: number
  branch: string
  qty: number
  qtyPerDay: number
  revenue: number
  estOnHand: number
  runwayDays: number | null
}
export interface StockOpportunity {
  skuId: number
  sku: string
  type: 'product' | 'medication'
  totalQty: number
  revenue: number
  fastBranch: string
  fastQty: number
  slowBranch: string
  slowQty: number
  ratio: number | null
  fromBranch: string
  toBranch: string
  estTransferQty: number
}
export interface StockoutSignal {
  skuId: number
  sku: string
  branch: string
  month: string
  normalQtyMo: number
  othersSelling: number
  estAtRisk: number
}
export interface StockOverviewResponse {
  window: { from: string; to: string; windowDays: number }
  coverageDays: number
  summary: {
    skusMoving: number
    branches: number
    opportunities: number
    stockouts: number
    estRevenueAtRisk: number
    totalRevenue: number
    totalUnits: number
  }
  velocity: StockVelocityRow[]
  opportunities: StockOpportunity[]
  stockouts: StockoutSignal[]
}
export interface StockTrendResponse {
  skuId: number
  sku: string | null
  type: string | null
  series: Array<{ ym: string; branch: string; qty: number; revenue: number }>
}

// ---- Real on-hand (staff weekly count) ----
export type StockSection = 'weight_loss' | 'booster_product' | 'skincare' | 'consumable' | 'other'
export interface StockOnHandCell {
  branchId: number
  branch: string
  freeFlow: number
  patientPaid: number
  owed: number
  consumedWeek: number | null
  runwayWeeks: number | null
  rawNote: string | null
}
export interface StockOnHandProduct {
  productId: number
  name: string
  section: StockSection
  unitType: string | null
  packSize: string | null
  sortNo: number | null
  branches: Record<number, StockOnHandCell>
  totals: { freeFlow: number; patientPaid: number; owed: number }
}
export interface StockOnHandStockout {
  productId: number
  name: string
  section: StockSection
  branch: string
  branchId: number
  othersWithStock: string[]
}
export interface StockOnHandResponse {
  latestDate: string | null
  priorDate: string | null
  branches: Array<{ id: number; name: string }>
  sections: StockSection[]
  summary: {
    products: number
    totalFreeFlow: number
    totalPatientPaid: number
    totalOwed: number
    stockouts: number
    lowRunway: number
  }
  products: StockOnHandProduct[]
  stockouts: StockOnHandStockout[]
}

// ============================================================================
// API Client
// ============================================================================
export const api = {
  health: {
    get: () => apiFetch<HealthResponse>('/health'),
    detailed: () => apiFetch<HealthDetailedResponse>('/health/detailed'),
    dbStats: () => apiFetch<DbStatsResponse>('/health/db-stats'),
    ready: () => apiFetch<{ ready: boolean }>('/health/ready'),
    live: () => apiFetch<{ live: boolean }>('/health/live'),
  },

  stock: {
    overview: (params?: StockOverviewParams) => {
      const sp = new URLSearchParams()
      if (params?.from) sp.set('from', params.from)
      if (params?.to) sp.set('to', params.to)
      if (params?.type) sp.set('type', params.type)
      if (params?.branchId) sp.set('branchId', String(params.branchId))
      if (params?.minRatio) sp.set('minRatio', String(params.minRatio))
      if (params?.coverageDays) sp.set('coverageDays', String(params.coverageDays))
      if (params?.months) sp.set('months', String(params.months))
      const q = sp.toString()
      return apiFetch<StockOverviewResponse>(`/stock/overview${q ? `?${q}` : ''}`)
    },
    skuTrend: (id: number, months = 6) =>
      apiFetch<StockTrendResponse>(`/stock/sku/${id}/trend?months=${months}`),
    onHand: (params?: { branchId?: number; section?: StockSection | 'all' }) => {
      const sp = new URLSearchParams()
      if (params?.branchId) sp.set('branchId', String(params.branchId))
      if (params?.section && params.section !== 'all') sp.set('section', params.section)
      const q = sp.toString()
      return apiFetch<StockOnHandResponse>(`/stock/on-hand${q ? `?${q}` : ''}`)
    },
  },

  analytics: {
    dashboard: (params?: { branchId?: string; year?: number; month?: number }) => {
      const searchParams = new URLSearchParams()
      if (params?.branchId) searchParams.set('branchId', params.branchId)
      if (params?.year) searchParams.set('year', params.year.toString())
      if (params?.month) searchParams.set('month', params.month.toString())
      const query = searchParams.toString()
      return apiFetch<DashboardStatsResponse>(`/analytics/dashboard${query ? `?${query}` : ''}`)
    },

    overview: (params?: AnalyticsOverviewParams) => {
      const searchParams = new URLSearchParams()
      if (params?.branchId) searchParams.set('branchId', params.branchId)
      if (params?.startDate) searchParams.set('startDate', params.startDate)
      if (params?.endDate) searchParams.set('endDate', params.endDate)
      if (params?.compareWithPrevious !== undefined)
        searchParams.set('compareWithPrevious', params.compareWithPrevious.toString())
      const query = searchParams.toString()
      return apiFetch<AnalyticsOverviewResponse>(`/analytics/overview${query ? `?${query}` : ''}`)
    },

    revenue: (params?: RevenueAnalyticsParams) => {
      const searchParams = new URLSearchParams()
      if (params?.branchId) searchParams.set('branchId', params.branchId)
      if (params?.startDate) searchParams.set('startDate', params.startDate)
      if (params?.endDate) searchParams.set('endDate', params.endDate)
      if (params?.groupBy) searchParams.set('groupBy', params.groupBy)
      const query = searchParams.toString()
      return apiFetch<RevenueAnalyticsResponse>(`/analytics/revenue${query ? `?${query}` : ''}`)
    },

    patients: (params?: PatientAnalyticsParams) => {
      const searchParams = new URLSearchParams()
      if (params?.branchId) searchParams.set('branchId', params.branchId)
      if (params?.startDate) searchParams.set('startDate', params.startDate)
      if (params?.endDate) searchParams.set('endDate', params.endDate)
      if (params?.inactiveMonths)
        searchParams.set('inactiveMonths', params.inactiveMonths.toString())
      const query = searchParams.toString()
      return apiFetch<PatientAnalyticsResponse>(`/analytics/patients${query ? `?${query}` : ''}`)
    },

    treatments: (params?: TreatmentAnalyticsParams) => {
      const searchParams = new URLSearchParams()
      if (params?.branchId) searchParams.set('branchId', params.branchId)
      if (params?.startDate) searchParams.set('startDate', params.startDate)
      if (params?.endDate) searchParams.set('endDate', params.endDate)
      if (params?.limit) searchParams.set('limit', params.limit.toString())
      const query = searchParams.toString()
      return apiFetch<TreatmentAnalyticsResponse>(
        `/analytics/treatments${query ? `?${query}` : ''}`
      )
    },

    reportTemplates: () => apiFetch<ReportTemplatesResponse>('/analytics/reports/templates'),

    generateReport: (params: ReportGenerateParams) =>
      apiFetch<ReportGenerateResponse>('/analytics/reports/generate', {
        method: 'POST',
        body: JSON.stringify(params),
      }),
  },

  patients: {
    list: (params?: PatientsListParams) => {
      const searchParams = new URLSearchParams()
      if (params?.branchId) searchParams.set('branchId', params.branchId.toString())
      if (params?.search) searchParams.set('search', params.search)
      if (params?.page) searchParams.set('page', params.page.toString())
      if (params?.pageSize) searchParams.set('pageSize', params.pageSize.toString())
      if (params?.sortBy) searchParams.set('sortBy', params.sortBy)
      if (params?.sortOrder) searchParams.set('sortOrder', params.sortOrder)
      if (params?.dateFilter) searchParams.set('dateFilter', params.dateFilter)
      if (params?.lastVisitFilter) searchParams.set('lastVisitFilter', params.lastVisitFilter)
      if (params?.hasPhone !== undefined) searchParams.set('hasPhone', params.hasPhone.toString())
      if (params?.waFilter && params.waFilter !== 'any')
        searchParams.set('waFilter', params.waFilter)
      const query = searchParams.toString()
      return apiFetch<PatientsListResponse>(`/patients${query ? `?${query}` : ''}`)
    },

    waSummary: (params?: PatientsWaSummaryParams) => {
      const searchParams = new URLSearchParams()
      if (params?.branchId) searchParams.set('branchId', params.branchId.toString())
      if (params?.lastVisitFilter) searchParams.set('lastVisitFilter', params.lastVisitFilter)
      if (params?.search) searchParams.set('search', params.search)
      const query = searchParams.toString()
      return apiFetch<PatientsWaSummary>(`/patients/wa-summary${query ? `?${query}` : ''}`)
    },

    get: (id: number) => apiFetch<PatientDetailResponse>(`/patients/${id}`),

    invoices: (id: number, params?: { page?: number; pageSize?: number }) => {
      const searchParams = new URLSearchParams()
      if (params?.page) searchParams.set('page', params.page.toString())
      if (params?.pageSize) searchParams.set('pageSize', params.pageSize.toString())
      const query = searchParams.toString()
      return apiFetch<PatientInvoicesResponse>(
        `/patients/${id}/invoices${query ? `?${query}` : ''}`
      )
    },

    setBlacklist: (id: number, data: PatientBlacklistInput) =>
      apiFetch<PatientBlacklistResponse>(`/patients/${id}/blacklist`, {
        method: 'PATCH',
        body: JSON.stringify(data),
      }),

    update: (id: number, data: UpdatePatientInput) =>
      apiFetch<{ data: Patient; message: string }>(`/patients/${id}`, {
        method: 'PATCH',
        body: JSON.stringify(data),
      }),

    export: (params?: PatientsExportParams) => {
      const searchParams = new URLSearchParams()
      if (params?.branchId) searchParams.set('branchId', params.branchId.toString())
      if (params?.lastVisitFilter) searchParams.set('lastVisitFilter', params.lastVisitFilter)
      if (params?.hasPhone !== undefined) searchParams.set('hasPhone', params.hasPhone.toString())
      if (params?.format) searchParams.set('format', params.format)
      if (params?.search) searchParams.set('search', params.search)
      if (params?.waFilter && params.waFilter !== 'any')
        searchParams.set('waFilter', params.waFilter)
      const query = searchParams.toString()
      return apiFetch<PatientsExportResponse>(`/patients/export${query ? `?${query}` : ''}`)
    },
  },

  appointments: {
    list: (params?: AppointmentsListParams) => {
      const searchParams = new URLSearchParams()
      if (params?.branchId) searchParams.set('branchId', params.branchId.toString())
      if (params?.from) searchParams.set('from', params.from)
      if (params?.to) searchParams.set('to', params.to)
      if (params?.status) searchParams.set('status', params.status)
      if (params?.attribution) searchParams.set('attribution', params.attribution)
      if (params?.creator) searchParams.set('creator', params.creator)
      if (params?.doctor) searchParams.set('doctor', params.doctor)
      if (params?.search) searchParams.set('search', params.search)
      if (params?.view) searchParams.set('view', params.view)
      if (params?.includeTasks) searchParams.set('includeTasks', 'true')
      if (params?.page) searchParams.set('page', params.page.toString())
      if (params?.pageSize) searchParams.set('pageSize', params.pageSize.toString())
      if (params?.sortBy) searchParams.set('sortBy', params.sortBy)
      if (params?.sortOrder) searchParams.set('sortOrder', params.sortOrder)
      const query = searchParams.toString()
      return apiFetch<AppointmentsListResponse>(`/appointments${query ? `?${query}` : ''}`)
    },

    get: (id: number) => apiFetch<AppointmentDetailResponse>(`/appointments/${id}`),

    filters: (branchId?: number) => {
      const query = branchId ? `?branchId=${branchId}` : ''
      return apiFetch<AppointmentFiltersResponse>(`/appointments/filters${query}`)
    },

    byPatient: (params: { pno?: string; patientId?: number; branchId?: number }) => {
      const searchParams = new URLSearchParams()
      if (params.pno) searchParams.set('pno', params.pno)
      if (params.patientId) searchParams.set('patientId', params.patientId.toString())
      if (params.branchId) searchParams.set('branchId', params.branchId.toString())
      return apiFetch<{ data: Appointment[]; total: number }>(
        `/appointments/by-patient?${searchParams.toString()}`
      )
    },
  },

  doctors: {
    list: (params?: DoctorsListParams) => {
      const sp = new URLSearchParams()
      if (params?.branchCode) sp.set('branchCode', params.branchCode)
      if (params?.kind) sp.set('kind', params.kind)
      if (params?.activeOnly) sp.set('activeOnly', 'true')
      const q = sp.toString()
      return apiFetch<DoctorsListResponse>(`/doctors${q ? `?${q}` : ''}`)
    },
    update: (id: number, data: { active?: boolean; notes?: string | null }) =>
      apiFetch<{ ok: boolean; data: { id: number; active: boolean; notes: string | null } }>(
        `/doctors/${id}`,
        { method: 'PATCH', body: JSON.stringify(data) }
      ),
  },

  slots: {
    /** Taken slots (bookings + blocks) + branch doctors for a branch/day. */
    taken: (branchCode: DoctorBranchCode, date: string) => {
      const sp = new URLSearchParams({ branchCode, date })
      return apiFetch<SlotsTakenResponse>(`/slots/taken?${sp.toString()}`)
    },
  },

  packages: {
    list: (params?: PackagesListParams) => {
      const sp = new URLSearchParams()
      if (params?.branchId) sp.set('branchId', String(params.branchId))
      if (params?.from) sp.set('from', params.from)
      if (params?.to) sp.set('to', params.to)
      if (params?.itemType) sp.set('itemType', params.itemType)
      if (params?.outstandingOnly !== undefined)
        sp.set('outstandingOnly', String(params.outstandingOnly))
      if (params?.search) sp.set('search', params.search)
      if (params?.view) sp.set('view', params.view)
      if (params?.page) sp.set('page', String(params.page))
      if (params?.pageSize) sp.set('pageSize', String(params.pageSize))
      if (params?.sortBy) sp.set('sortBy', params.sortBy)
      if (params?.sortOrder) sp.set('sortOrder', params.sortOrder)
      const query = sp.toString()
      return apiFetch<PackagesListResponse>(`/packages${query ? `?${query}` : ''}`)
    },
    getExportUrl: (params?: PackagesListParams) => {
      const sp = new URLSearchParams()
      if (params?.branchId) sp.set('branchId', String(params.branchId))
      if (params?.from) sp.set('from', params.from)
      if (params?.to) sp.set('to', params.to)
      if (params?.itemType) sp.set('itemType', params.itemType)
      if (params?.outstandingOnly !== undefined)
        sp.set('outstandingOnly', String(params.outstandingOnly))
      if (params?.search) sp.set('search', params.search)
      const query = sp.toString()
      return `${API_BASE}/packages/export${query ? `?${query}` : ''}`
    },
  },

  followUps: {
    summary: (params?: FollowUpSummaryParams) => {
      const sp = new URLSearchParams()
      if (params?.branchId) sp.set('branchId', String(params.branchId))
      if (params?.status) sp.set('status', params.status)
      if (params?.assignedTo) sp.set('assignedTo', String(params.assignedTo))
      if (params?.from) sp.set('from', params.from)
      if (params?.to) sp.set('to', params.to)
      const q = sp.toString()
      return apiFetch<FollowUpSummaryResponse>(`/follow-ups/summary${q ? `?${q}` : ''}`)
    },

    list: (params?: FollowUpsListParams) => {
      const sp = new URLSearchParams()
      if (params?.branchId) sp.set('branchId', String(params.branchId))
      if (params?.queue) sp.set('queue', params.queue)
      if (params?.status) sp.set('status', params.status)
      if (params?.assignedTo) sp.set('assignedTo', String(params.assignedTo))
      if (params?.from) sp.set('from', params.from)
      if (params?.to) sp.set('to', params.to)
      if (params?.search) sp.set('search', params.search)
      if (params?.sort) sp.set('sort', params.sort)
      if (params?.page) sp.set('page', String(params.page))
      if (params?.pageSize) sp.set('pageSize', String(params.pageSize))
      const q = sp.toString()
      return apiFetch<FollowUpsListResponse>(`/follow-ups${q ? `?${q}` : ''}`)
    },

    assignees: (branchId?: number) => {
      const q = branchId ? `?branchId=${branchId}` : ''
      return apiFetch<FollowUpAssigneesResponse>(`/follow-ups/assignees${q}`)
    },

    history: (params: FollowUpHistoryParams) => {
      const sp = new URLSearchParams()
      if (params.branchId) sp.set('branchId', String(params.branchId))
      sp.set('month', params.month)
      if (params.mode) sp.set('mode', params.mode)
      if (params.staffId) sp.set('staffId', String(params.staffId))
      if (params.queue) sp.set('queue', params.queue)
      if (params.source) sp.set('source', params.source)
      if (params.hideNoise !== undefined) sp.set('hideNoise', String(params.hideNoise))
      if (params.search) sp.set('search', params.search)
      if (params.page) sp.set('page', String(params.page))
      if (params.pageSize) sp.set('pageSize', String(params.pageSize))
      return apiFetch<FollowUpHistory>(`/follow-ups/history?${sp.toString()}`)
    },

    byPatient: (patientId: number) =>
      apiFetch<FollowUpByPatientResponse>(`/follow-ups/patient/${patientId}`),

    get: (id: number) => apiFetch<FollowUpDetailResponse>(`/follow-ups/${id}`),

    script: (id: number) => apiFetch<FollowUpScriptResponse>(`/follow-ups/${id}/script`),

    assign: (id: number, assignedTo: number | null) =>
      apiFetch<{ data: FollowUpRow }>(`/follow-ups/${id}/assign`, {
        method: 'PATCH',
        body: JSON.stringify({ assignedTo }),
      }),

    addEvent: (id: number, data: FollowUpAddEventInput) =>
      apiFetch<{ data: FollowUpRow; event: FollowUpEvent }>(`/follow-ups/${id}/events`, {
        method: 'POST',
        body: JSON.stringify(data),
      }),

    generate: (data?: { branchId?: number }) =>
      apiFetch<FollowUpGenerateResponse>('/follow-ups/generate', {
        method: 'POST',
        body: JSON.stringify(data ?? {}),
      }),
  },

  redemption: {
    list: (params?: RedemptionListParams) => {
      const sp = new URLSearchParams()
      if (params?.branchId) sp.set('branchId', String(params.branchId))
      if (params?.riskTier && params.riskTier !== 'all') sp.set('riskTier', params.riskTier)
      if (params?.search) sp.set('search', params.search)
      if (params?.treatmentMasterId) sp.set('treatmentMasterId', String(params.treatmentMasterId))
      if (params?.minOutstanding) sp.set('minOutstanding', String(params.minOutstanding))
      if (params?.page) sp.set('page', String(params.page))
      if (params?.pageSize) sp.set('pageSize', String(params.pageSize))
      if (params?.sortBy) sp.set('sortBy', params.sortBy)
      if (params?.sortOrder) sp.set('sortOrder', params.sortOrder)
      const q = sp.toString()
      return apiFetch<RedemptionListResponse>(`/redemption${q ? `?${q}` : ''}`)
    },
    get: (patientId: number) => apiFetch<RedemptionDetailResponse>(`/redemption/${patientId}`),
  },

  invoices: {
    list: (params?: InvoicesListParams) => {
      const searchParams = new URLSearchParams()
      if (params?.branchId) searchParams.set('branchId', params.branchId.toString())
      if (params?.search) searchParams.set('search', params.search)
      if (params?.page) searchParams.set('page', params.page.toString())
      if (params?.pageSize) searchParams.set('pageSize', params.pageSize.toString())
      if (params?.sortBy) searchParams.set('sortBy', params.sortBy)
      if (params?.sortOrder) searchParams.set('sortOrder', params.sortOrder)
      if (params?.dateFilter) searchParams.set('dateFilter', params.dateFilter)
      if (params?.status) searchParams.set('status', params.status)
      if (params?.patientType) searchParams.set('patientType', params.patientType)
      const query = searchParams.toString()
      return apiFetch<InvoicesListResponse>(`/invoices${query ? `?${query}` : ''}`)
    },

    get: (id: number) => apiFetch<InvoiceDetailResponse>(`/invoices/${id}`),

    items: (id: number, params?: { page?: number; pageSize?: number }) => {
      const searchParams = new URLSearchParams()
      if (params?.page) searchParams.set('page', params.page.toString())
      if (params?.pageSize) searchParams.set('pageSize', params.pageSize.toString())
      const query = searchParams.toString()
      return apiFetch<InvoiceItemsResponse>(`/invoices/${id}/items${query ? `?${query}` : ''}`)
    },
  },

  treatments: {
    list: (params?: TreatmentsListParams) => {
      const searchParams = new URLSearchParams()
      if (params?.page) searchParams.set('page', params.page.toString())
      if (params?.pageSize) searchParams.set('pageSize', params.pageSize.toString())
      if (params?.branchId) searchParams.set('branchId', params.branchId.toString())
      if (params?.categoryId) searchParams.set('categoryId', params.categoryId.toString())
      if (params?.type) searchParams.set('type', params.type)
      if (params?.search) searchParams.set('search', params.search)
      if (params?.sortBy) searchParams.set('sortBy', params.sortBy)
      if (params?.sortOrder) searchParams.set('sortOrder', params.sortOrder)
      if (params?.dateFilter) searchParams.set('dateFilter', params.dateFilter)
      const query = searchParams.toString()
      return apiFetch<TreatmentsListResponse>(`/treatments${query ? `?${query}` : ''}`)
    },

    get: (id: number) => apiFetch<TreatmentDetail>(`/treatments/${id}`),

    categories: () => apiFetch<{ data: TreatmentCategory[] }>('/treatments/categories'),

    stats: () => apiFetch<TreatmentStats>('/treatments/stats'),

    variations: (id: number, params?: { page?: number; pageSize?: number }) => {
      const searchParams = new URLSearchParams()
      if (params?.page) searchParams.set('page', params.page.toString())
      if (params?.pageSize) searchParams.set('pageSize', params.pageSize.toString())
      const query = searchParams.toString()
      return apiFetch<TreatmentVariationsResponse>(
        `/treatments/${id}/variations${query ? `?${query}` : ''}`
      )
    },

    unmatched: (params?: { page?: number; pageSize?: number }) => {
      const searchParams = new URLSearchParams()
      if (params?.page) searchParams.set('page', params.page.toString())
      if (params?.pageSize) searchParams.set('pageSize', params.pageSize.toString())
      const query = searchParams.toString()
      return apiFetch<UnmatchedListResponse>(`/treatments/unmatched${query ? `?${query}` : ''}`)
    },

    createVariation: (data: CreateVariationInput) =>
      apiFetch<{ success: boolean }>('/treatments/variations', {
        method: 'POST',
        body: JSON.stringify(data),
      }),

    analyticsSummary: (branchId?: number) => {
      const query = branchId ? `?branchId=${branchId}` : ''
      return apiFetch<TreatmentAnalyticsSummary>(`/treatments/analytics/summary${query}`)
    },

    analyticsTop: (params?: { branchId?: number; limit?: number }) => {
      const searchParams = new URLSearchParams()
      if (params?.branchId) searchParams.set('branchId', params.branchId.toString())
      if (params?.limit) searchParams.set('limit', params.limit.toString())
      const query = searchParams.toString()
      return apiFetch<{ data: TopTreatment[] }>(
        `/treatments/analytics/top${query ? `?${query}` : ''}`
      )
    },

    analyticsByCategory: (branchId?: number) => {
      const query = branchId ? `?branchId=${branchId}` : ''
      return apiFetch<{ data: CategoryRevenue[] }>(`/treatments/analytics/by-category${query}`)
    },

    invoices: (id: number, params?: { page?: number; pageSize?: number }) => {
      const searchParams = new URLSearchParams()
      if (params?.page) searchParams.set('page', params.page.toString())
      if (params?.pageSize) searchParams.set('pageSize', params.pageSize.toString())
      const query = searchParams.toString()
      return apiFetch<TreatmentInvoicesResponse>(
        `/treatments/${id}/invoices${query ? `?${query}` : ''}`
      )
    },
  },

  knowledgeBase: {
    stats: () => apiFetch<KnowledgeBaseStatsResponse>('/knowledge-base/stats'),

    list: (params?: KnowledgeBaseListParams) => {
      const searchParams = new URLSearchParams()
      if (params?.page) searchParams.set('page', params.page.toString())
      if (params?.limit) searchParams.set('limit', params.limit.toString())
      if (params?.search) searchParams.set('search', params.search)
      if (params?.docType) searchParams.set('docType', params.docType)
      if (params?.branchId) searchParams.set('branchId', params.branchId.toString())
      if (params?.isActive !== undefined) searchParams.set('isActive', params.isActive.toString())
      const query = searchParams.toString()
      return apiFetch<KnowledgeBaseListResponse>(`/knowledge-base${query ? `?${query}` : ''}`)
    },

    get: (id: number) => apiFetch<KnowledgeBaseDocumentResponse>(`/knowledge-base/${id}`),

    create: (data: KnowledgeBaseCreateInput) =>
      apiFetch<KnowledgeBaseDocumentResponse>('/knowledge-base', {
        method: 'POST',
        body: JSON.stringify(data),
      }),

    update: (id: number, data: KnowledgeBaseUpdateInput) =>
      apiFetch<KnowledgeBaseDocumentResponse>(`/knowledge-base/${id}`, {
        method: 'PATCH',
        body: JSON.stringify(data),
      }),

    delete: (id: number) =>
      apiFetch<{ success: boolean }>(`/knowledge-base/${id}`, {
        method: 'DELETE',
      }),

    search: (params: KnowledgeBaseSearchParams) => {
      const searchParams = new URLSearchParams()
      searchParams.set('query', params.query)
      if (params.limit) searchParams.set('limit', params.limit.toString())
      if (params.branchId) searchParams.set('branchId', params.branchId.toString())
      if (params.docType) searchParams.set('docType', params.docType)
      if (params.threshold) searchParams.set('threshold', params.threshold.toString())
      return apiFetch<KnowledgeBaseSearchResponse>(
        `/knowledge-base/search?${searchParams.toString()}`
      )
    },

    context: (data: { query: string; maxTokens?: number; branchId?: number }) =>
      apiFetch<KnowledgeBaseContextResponse>('/knowledge-base/context', {
        method: 'POST',
        body: JSON.stringify(data),
      }),
  },
}

// ============================================================================
// Other Response Types
// ============================================================================
export interface HealthResponse {
  status: 'healthy' | 'unhealthy'
  timestamp: string
  version: string
}

export interface HealthDetailedResponse {
  status: 'healthy' | 'degraded' | 'unhealthy'
  timestamp: string
  version: string
  checks: Record<
    string,
    {
      status: 'healthy' | 'unhealthy' | 'not_configured'
      latency?: number
      error?: string
      message?: string
    }
  >
}

export interface DbStatsResponse {
  status: 'ok' | 'error'
  tables: {
    branches: number
    patients: number
    invoices: number
    invoice_items: number
  }
}

export interface DashboardStatsResponse {
  period: {
    year: number
    month: number
    monthName: string
  }
  branchId: string
  stats: {
    existingPatients: number
    newPatients: number
    totalInvoices: number
    totalRevenue: number
    newRevenue: number
    existingRevenue: number
  }
  branchSummary: {
    id: number
    code: string
    name: string
    patients: number
    invoices: number
  }[]
}

// ============================================================================
// Analytics Overview Types
// ============================================================================
export interface AnalyticsOverviewParams {
  branchId?: string
  startDate?: string
  endDate?: string
  compareWithPrevious?: boolean
}

export interface KpiMetric {
  value: number
  growth: number | null
}

export interface RevenueTrendItem {
  month: string
  label: string
  revenue: number
}

export interface BranchComparisonItem {
  id: number
  code: string
  name: string
  revenue: number
  invoices: number
  patients: number
  percentage: number
}

export interface PatientTypeItem {
  type: string
  count: number
  revenue: number
}

export interface TopTreatmentItem {
  name: string
  category: string | null
  count: number
  revenue: number
}

export interface AnalyticsOverviewResponse {
  period: {
    startDate: string
    endDate: string
  }
  branchId: string
  kpis: {
    revenue: KpiMetric
    patients: KpiMetric
    treatments: KpiMetric
    avgTransaction: KpiMetric
  }
  charts: {
    revenueTrend: RevenueTrendItem[]
    branchComparison: BranchComparisonItem[]
    patientTypes: PatientTypeItem[]
    topTreatments: TopTreatmentItem[]
  }
}

// ============================================================================
// Revenue Analytics Types
// ============================================================================
export interface RevenueAnalyticsParams {
  branchId?: string
  startDate?: string
  endDate?: string
  groupBy?: 'day' | 'week' | 'month'
}

export interface RevenueTrendDataItem {
  period: string
  label: string
  date: string
  revenue: number
  invoices: number
  patients: number
}

export interface RevenueBranchItem {
  id: number
  code: string
  name: string
  revenue: number
  invoices: number
  percentage: number
}

export interface RevenueCategoryItem {
  category: string
  revenue: number
  count: number
  percentage: number
}

export interface MonthlyComparisonItem {
  year: number
  month: number
  label: string
  revenue: number
  invoices: number
  patients: number
  avgTransaction: number
  growth: number | null
}

export interface RevenueAnalyticsResponse {
  period: {
    startDate: string
    endDate: string
    groupBy: 'day' | 'week' | 'month'
  }
  branchId: string
  summary: {
    totalRevenue: number
    totalInvoices: number
    avgDaily: number
    avgTransaction: number
  }
  charts: {
    revenueTrend: RevenueTrendDataItem[]
    byBranch: RevenueBranchItem[]
    byCategory: RevenueCategoryItem[]
  }
  monthlyComparison: MonthlyComparisonItem[]
}

// ============================================================================
// Patient Analytics Types
// ============================================================================
export interface PatientAnalyticsParams {
  branchId?: string
  startDate?: string
  endDate?: string
  inactiveMonths?: number
}

export interface AcquisitionTrendItem {
  month: string
  label: string
  newPatients: number
  totalPatients: number
}

export interface VisitFrequencyItem {
  range: string
  count: number
  percentage: number
}

export interface PatientsByBranchItem {
  id: number
  code: string
  name: string
  count: number
  percentage: number
}

export interface TopPatientItem {
  id: number
  name: string
  pno: string
  phone: string | null
  branchName: string | null
  totalVisits: number
  totalSpent: number
  lastVisit: string
}

export interface InactivePatientItem {
  id: number
  name: string
  pno: string
  phone: string | null
  branchName: string | null
  lastVisit: string
  daysSinceVisit: number
  totalSpent: number
}

export interface PatientAnalyticsResponse {
  period: {
    startDate: string
    endDate: string
  }
  branchId: string
  summary: {
    totalPatients: number
    newPatients: number
    returningPatients: number
    avgVisitsPerPatient: number
    inactiveCount: number
  }
  charts: {
    acquisitionTrend: AcquisitionTrendItem[]
    visitFrequency: VisitFrequencyItem[]
    patientsByBranch: PatientsByBranchItem[]
  }
  topPatients: TopPatientItem[]
  inactivePatients: InactivePatientItem[]
}

// ============================================================================
// Treatment Analytics Types
// ============================================================================
export interface TreatmentAnalyticsParams {
  branchId?: string
  startDate?: string
  endDate?: string
  limit?: number
}

export interface PopularTreatmentItem {
  rank: number
  name: string
  treatmentId: number | null
  category: string
  type: string
  count: number
  revenue: number
  avgPrice: number
}

export interface CategoryBreakdownItem {
  category: string
  count: number
  revenue: number
  percentage: number
}

export interface TreatmentTrendItem {
  month: string
  label: string
  treatmentCount: number
  uniqueTreatments: number
  revenue: number
}

export interface TreatmentTypeItem {
  type: string
  count: number
  revenue: number
  percentage: number
}

export interface TreatmentsByBranchItem {
  id: number
  code: string
  name: string
  treatmentCount: number
  revenue: number
  percentage: number
}

export interface CrossSellItem {
  treatment1: string
  treatment2: string
  pairCount: number
}

export interface TreatmentAnalyticsResponse {
  period: {
    startDate: string
    endDate: string
  }
  branchId: string
  summary: {
    totalTreatments: number
    uniqueTreatments: number
    totalRevenue: number
    avgRevenuePerTreatment: number
    matchedCount: number
    matchRate: number
    topCategory: string
  }
  charts: {
    popularTreatments: PopularTreatmentItem[]
    categoryBreakdown: CategoryBreakdownItem[]
    treatmentTrend: TreatmentTrendItem[]
    treatmentTypes: TreatmentTypeItem[]
    treatmentsByBranch: TreatmentsByBranchItem[]
  }
  crossSellAnalysis: CrossSellItem[]
}

// ============================================================================
// Report Types
// ============================================================================
export type ReportTemplateId =
  | 'monthly-revenue'
  | 'patient-analytics'
  | 'treatment-performance'
  | 'branch-comparison'
  | 'financial-summary'
  | 'marketing-insights'

export interface ReportTemplate {
  id: ReportTemplateId
  name: string
  description: string
  category: string
  icon: string
}

export interface ReportGenerateParams {
  templateId: ReportTemplateId
  branchId?: string
  startDate: string
  endDate: string
}

export interface MonthlyRevenueReportData {
  summary: {
    totalRevenue: number
    totalInvoices: number
    totalPatients: number
    avgMonthlyRevenue: number
    avgTransaction: number
  }
  monthlyBreakdown: {
    year: number
    month: number
    label: string
    revenue: number
    invoices: number
    patients: number
    avgTransaction: number
    growth: number | null
  }[]
}

export interface PatientAnalyticsReportData {
  summary: {
    totalPatients: number
    newPatients: number
    returningPatients: number
    avgVisits: number
    retentionRate: number
  }
  acquisitionTrend: {
    label: string
    newPatients: number
  }[]
  topPatients: {
    name: string
    pno: string
    visits: number
    totalSpent: number
  }[]
}

export interface TreatmentPerformanceReportData {
  summary: {
    totalTreatments: number
    uniqueTreatments: number
    totalRevenue: number
    avgPrice: number
  }
  topTreatments: {
    name: string
    category: string
    count: number
    revenue: number
  }[]
  categoryBreakdown: {
    category: string
    count: number
    revenue: number
  }[]
}

export interface BranchComparisonReportData {
  summary: {
    totalBranches: number
    totalRevenue: number
    totalInvoices: number
    totalPatients: number
    topBranch: string
  }
  branches: {
    id: number
    code: string
    name: string
    revenue: number
    invoices: number
    patients: number
    newPatients: number
    avgTransaction: number
    revenuePercent: number
  }[]
}

export interface FinancialSummaryReportData {
  summary: {
    totalRevenue: number
    totalInvoices: number
    uniquePatients: number
    avgTransaction: number
    minTransaction: number
    maxTransaction: number
    avgDailyRevenue: number
    avgDailyInvoices: number
    daysInPeriod: number
  }
  revenueByCategory: {
    category: string
    revenue: number
    percentage: number
  }[]
}

export interface MarketingInsightsReportData {
  summary: {
    totalNewPatients: number
    totalReturningPatients: number
    acquisitionRate: number
    retentionRate: number
  }
  acquisitionTrend: {
    label: string
    newPatients: number
    returningPatients: number
  }[]
  inactivePatients: {
    name: string
    pno: string
    phone: string | null
    lastVisit: string
    daysSince: number
    totalSpent: number
  }[]
}

export type ReportData =
  | MonthlyRevenueReportData
  | PatientAnalyticsReportData
  | TreatmentPerformanceReportData
  | BranchComparisonReportData
  | FinancialSummaryReportData
  | MarketingInsightsReportData

export interface GeneratedReport {
  template: ReportTemplate
  period: {
    startDate: string
    endDate: string
  }
  branchId: string
  generatedAt: string
  data: ReportData
}

export interface ReportTemplatesResponse {
  templates: ReportTemplate[]
}

export interface ReportGenerateResponse {
  success: boolean
  report: GeneratedReport
  error?: string
}

// ============================================================================
// Invoice Types
// ============================================================================
export interface Invoice {
  id: number
  branchId: number
  branchCode: string | null
  branchName: string | null
  patientId: number | null
  patientName: string | null
  patientPno: string | null
  invoiceNo: string
  accountNo: string
  invoiceDate: string
  status: string | null
  patientType: string | null
  createdBy: string | null
  inventoryAmount: string | null
  servicesAmount: string | null
  discountAmount: string | null
  subtotal: string | null
  grandTotal: string | null
  balance: string | null
  paidAmount: string | null
  paymentMethod: string | null
  paymentDate: string | null
  createdAt: string
  updatedAt?: string
}

export interface InvoiceDetail extends Invoice {
  patientNric: string | null
  patientPhone: string | null
}

export interface InvoiceItem {
  id: number
  lineNumber: number | null
  rawTreatmentName: string
  quantity: string | null
  unitPrice: string | null
  discount: string | null
  totalPrice: string | null
  treatmentId: number | null
  matchConfidence: string | null
  matchMethod: string | null
  isFoc: boolean | null
  isPromo: boolean | null
  promoCode: string | null
}

export interface InvoicesListParams {
  branchId?: number
  search?: string
  page?: number
  pageSize?: number
  sortBy?: 'invoiceNo' | 'invoiceDate' | 'grandTotal' | 'patientName' | 'createdAt'
  sortOrder?: 'asc' | 'desc'
  dateFilter?: 'today' | 'week' | 'month' | 'lastMonth' | 'last3Months' | 'year'
  status?: 'Paid' | 'Pending' | 'Void'
  patientType?: 'New' | 'Existing'
}

export interface InvoicesListResponse {
  data: Invoice[]
  total: number
  page: number
  pageSize: number
  pageCount: number
}

export interface InvoiceDetailResponse {
  data: InvoiceDetail
  items: InvoiceItem[]
  itemStats: {
    totalItems: number
    totalFoc: number
    totalPromo: number
  }
}

export interface InvoiceItemsResponse {
  data: InvoiceItem[]
  total: number
  page: number
  pageSize: number
  pageCount: number
}

// ============================================================================
// Appointment Types
// ============================================================================
export type AppointmentStatus = 'turn_up' | 'did_not_turn_up' | 'cancelled'
export type AppointmentAttribution = 'closer' | 'in-clinic' | 'all'
export type AppointmentView = 'rows' | 'patients'

export interface Appointment {
  id: number
  branchId: number
  branchCode: string | null
  branchName: string | null
  appointmentAt: string
  timeIn: string | null
  timeOut: string | null
  createdBy: string | null
  doctor: string | null
  pno: string | null
  patientId: number | null
  patientLinkedName: string | null
  patientName: string | null
  patientNric: string | null
  patientType: string | null
  type: string | null
  status: AppointmentStatus
  cancelComment: string | null
  invoiceNo: string | null
  invoiceAmount: string | null
  visitReasonCategory: string | null
  visitReasonDetail: string | null
  isTask: boolean
  scrapedAt: string
}

export interface AppointmentDetail extends Appointment {
  patientLinkedDisplayName: string | null
  patientLinkedPhone: string | null
  createdAt: string
}

export interface PatientVisit {
  branchId: number
  visitDate: string
  patientKey: string
  patientId: number | null
  patientName: string | null
  pno: string | null
  patientNric: string | null
  patientType: string | null
  creators: (string | null)[]
  doctors: (string | null)[]
  statuses: string[]
  slots: string[]
  reasons: (string | null)[]
  hasTurnUp: boolean
  hasCancel: boolean
  hasInvoice: boolean
  totalInvoice: string
  slotCount: number
  attribution: 'closer' | 'in-clinic'
  outcome: 'attended' | 'cancelled' | 'likely_redemption' | 'no_show' | 'mixed'
}

export interface AppointmentsListParams {
  branchId?: number
  from?: string
  to?: string
  status?: string
  attribution?: AppointmentAttribution
  creator?: string
  doctor?: string
  search?: string
  view?: AppointmentView
  includeTasks?: boolean
  page?: number
  pageSize?: number
  sortBy?: 'appointmentAt' | 'createdBy' | 'doctor' | 'status' | 'patientName'
  sortOrder?: 'asc' | 'desc'
}

export interface AppointmentsRowsResponse {
  view: 'rows'
  data: Appointment[]
  total: number
  /** Same query but stripped of date/status/creator/doctor/search/attribution.
   * Used by the frontend "X of Y rows hidden" banner. Branch + tasks toggle
   * are kept (those are scope, not user filters). */
  unfilteredTotal: number
  page: number
  pageSize: number
  pageCount: number
  summary: {
    total: number
    turnUp: string
    dntu: string
    cancelled: string
    uniquePatients: string
    totalRevenue: number
    newSales: string
    realNoShows: string
    closerCount: string
  }
}

export interface AppointmentsPatientsResponse {
  view: 'patients'
  data: PatientVisit[]
  total: number
  unfilteredTotal: number
  page: number
  pageSize: number
  pageCount: number
  summary: {
    uniquePatients: number
    attended: string
    likelyRedemption: string
    noShow: string
    cancelled: string
    mixed: string
    totalRevenue: number
    closerDriven: string
    inClinicDriven: string
  }
}

export type AppointmentsListResponse = AppointmentsRowsResponse | AppointmentsPatientsResponse

export interface AppointmentDetailResponse {
  data: AppointmentDetail
  linkedInvoice: { id: number; grandTotal: string | null; invoiceDate: string | null } | null
}

export interface AppointmentFiltersResponse {
  creators: string[]
  doctors: string[]
  closerSet: readonly string[]
}

// ============================================================================
// Doctor Roster Types (Avixo #activeDoctorColumns)
// ============================================================================
export type DoctorKind = 'doctor' | 'assistant' | 'virtual'
export type DoctorBranchCode = 'shah-alam' | 'putrajaya' | 'bangi' | 'kuala-lumpur'

export interface DoctorsListParams {
  branchCode?: DoctorBranchCode
  kind?: DoctorKind
  activeOnly?: boolean
}

export interface DoctorRow {
  id: number
  branchId: number
  branchCode: string
  branchName: string | null
  name: string
  doctorKey: string
  avixoLaneId: number | null
  kind: DoctorKind
  sortOrder: number | null
  active: boolean
  notes: string | null
  syncedAt: string
  isFloating: boolean
}

export interface DoctorsListResponse {
  data: DoctorRow[]
  total: number
}

// ============================================================================
// Slots / Calendar-grid Types (Avixo calendar_events → GET /slots/taken)
// ============================================================================
/** One active Dr* lane for the branch (from the `doctors` roster). */
export interface BranchDoctor {
  doctorKey: string
  name: string
}

/** One calendar event for the day — a real booking (isNonPatient=false) or a
 * background block (isNonPatient=true). Times are MYT wall-clock "HH:MM". */
export interface TakenEvent {
  start: string
  end: string | null
  doctorName: string | null
  /** Alias-aware normalized key; matches a BranchDoctor.doctorKey (or null). */
  doctorKey: string | null
  isNonPatient: boolean
  /** Patient name for real bookings. */
  patientName: string | null
  /** Block label (ACB / MC / NO DOCTOR / …) for blocks; visit note otherwise. */
  content: string | null
  /** Procedure / visit reason (e.g. "Pelvic Care") — tooltip detail. */
  visitReason: string | null
  /** Patient phone — tooltip detail. */
  mobile: string | null
  /** Free-text booking comment from Avixo — tooltip detail. */
  comment: string | null
}

export interface SlotsTakenResponse {
  branchCode: DoctorBranchCode
  date: string
  /** MYT weekday, 0=Sun … 6=Sat. */
  weekday: number
  /** Clinic-level closure — Monday or a whole-branch holiday exception. */
  closed: boolean
  /** MAX(synced_at) as UTC ISO with Z, or null when no rows for the day. */
  syncedAt: string | null
  branchDoctors: BranchDoctor[]
  /** Assistant / virtual / custom-column lanes — rendered as their own grid
   * columns even on days with no events. Not used for availability. */
  otherLanes?: Array<BranchDoctor & { kind: 'assistant' | 'virtual' | 'doctor' }>
  /** Transitional alias of branchDoctors (kept for older consumers). */
  rosteredDoctors: BranchDoctor[]
  taken: TakenEvent[]
}

// ============================================================================
// Package Redemption Types (Avixo Packages report)
// ============================================================================
export type PackageItemType = 'treatment' | 'consultation' | 'foc'
export type PackageView = 'rows' | 'patients'

export interface PackagesListParams {
  branchId?: number
  from?: string
  to?: string
  /** Comma list of item types, e.g. "treatment,consultation". */
  itemType?: string
  outstandingOnly?: boolean
  search?: string
  view?: PackageView
  page?: number
  pageSize?: number
  sortBy?:
    | 'purchaseDate'
    | 'balance'
    | 'patientName'
    | 'packageName'
    | 'treatmentBalance'
    | 'totalBalance'
  sortOrder?: 'asc' | 'desc'
}

export interface PackageRow {
  id: number
  pno: string
  patientName: string | null
  phone: string | null
  patientId: number | null
  purchaseDate: string
  invoiceNo: string
  packageName: string
  itemName: string
  itemType: PackageItemType
  boughtQty: number
  usedQty: number
  balanceQty: number
  packagePrice: number | null
  branchId: number
  branchName: string | null
}

export interface PackagePatientRow {
  branchId: number
  branchName: string | null
  pno: string
  patientName: string | null
  phone: string | null
  patientId: number | null
  packageCount: number
  treatmentBalance: number
  totalBalance: number
  lastPurchase: string | null
}

export interface PackagesSummary {
  patients: number
  outstandingItems: number
  packages: number
  treatmentBalance: number
  totalBalance: number
  unearnedRevenue: number
}

export interface PackagesRowsResponse {
  view: 'rows'
  data: PackageRow[]
  total: number
  unfilteredTotal: number
  page: number
  pageSize: number
  pageCount: number
  summary: PackagesSummary
}

export interface PackagesPatientsResponse {
  view: 'patients'
  data: PackagePatientRow[]
  total: number
  unfilteredTotal: number
  page: number
  pageSize: number
  pageCount: number
  summary: PackagesSummary
}

export type PackagesListResponse = PackagesRowsResponse | PackagesPatientsResponse

// ============================================================================
// Treatment Types
// ============================================================================
export type TreatmentType = 'treatment' | 'product' | 'medication'
export type MatchMethod = 'exact' | 'normalized' | 'contains' | 'fuzzy' | 'manual' | 'ai'

export interface Treatment {
  id: number
  name: string
  displayName: string | null
  type: TreatmentType
  aliases: string[] | null
  keywords: string | null
  isActive: boolean
  categoryId: number | null
  categoryName: string | null
  variationCount: number
  itemCount: number
  revenue: number
}

export interface TreatmentDetail extends Treatment {
  description: string | null
  basePrice: number | null
  bodyArea: string | null
  createdAt: string
  updatedAt: string | null
}

export interface TreatmentCategory {
  id: number
  name: string
  description: string | null
  displayOrder: number
  isActive: boolean
  treatmentCount: number
}

export interface TreatmentVariation {
  id: number
  variationName: string
  normalizedName: string
  matchType: MatchMethod
  confidence: number
  createdBy: string
  occurrenceCount: number
  totalRevenue: number
  promoCode: string | null
  sessionCount: number | null
  isFoc: boolean
  isPackage: boolean
  branchName: string | null
  firstSeenDate: string | null
}

export interface UnmatchedItem {
  name: string
  count: number
  revenue: number
}

export interface TreatmentStats {
  totalItems: number
  matchedItems: number
  unmatchedItems: number
  matchRate: number
  uniqueUnmatched: number
  treatmentCount: number
  variationCount: number
}

export interface TreatmentsListParams {
  page?: number
  pageSize?: number
  branchId?: number
  categoryId?: number
  type?: TreatmentType
  isActive?: boolean
  search?: string
  sortBy?: 'name' | 'itemCount' | 'revenue' | 'variationCount'
  sortOrder?: 'asc' | 'desc'
  dateFilter?: 'today' | 'week' | 'month' | 'lastMonth' | 'last3Months' | 'year'
}

export interface TreatmentsListResponse {
  data: Treatment[]
  total: number
  page: number
  pageSize: number
  totalPages: number
}

export interface TreatmentVariationsResponse {
  data: TreatmentVariation[]
  total: number
  page: number
  pageSize: number
}

export interface TreatmentInvoice {
  id: number
  invoiceNo: string
  invoiceDate: string
  grandTotal: number
  patientType: string
  patientName: string | null
  patientPno: string | null
  branchName: string
  itemTotal: number
  itemName: string
}

export interface TreatmentInvoicesResponse {
  data: TreatmentInvoice[]
  total: number
  page: number
  pageSize: number
  totalPages: number
}

export interface UnmatchedListResponse {
  data: UnmatchedItem[]
  total: number
  page: number
  pageSize: number
}

export interface TreatmentAnalyticsSummary {
  totalRevenue: number
  uniqueTreatments: number
  totalItems: number
  avgPerItem: number
  topTreatment: string | null
  categoryCount: number
}

export interface TopTreatment {
  id: number
  name: string
  categoryName: string | null
  itemCount: number
  revenue: number
}

export interface CategoryRevenue {
  categoryName: string
  itemCount: number
  revenue: number
}

export interface CreateVariationInput {
  variationName: string
  treatmentId: number
  matchType?: MatchMethod
  confidence?: number
}

// ============================================================================
// Knowledge Base Types
// ============================================================================
export type DocumentType = 'sop' | 'faq' | 'policy' | 'manual' | 'training' | 'other'

export interface KnowledgeBaseDocument {
  id: number
  title: string
  description: string | null
  fileName: string | null
  fileType: string | null
  fileSize: number | null
  docType: DocumentType | null
  branchId: number | null
  content: string
  metadata: string | null
  isActive: boolean
  createdAt: string
  updatedAt: string
  chunkCount?: number
}

export interface KnowledgeBaseChunk {
  id: number
  content: string
  chunkIndex: number
  tokenCount: number | null
}

export interface KnowledgeBaseDocumentWithChunks extends KnowledgeBaseDocument {
  chunks: KnowledgeBaseChunk[]
}

export interface KnowledgeBaseSearchResult {
  chunkId: number
  content: string
  chunkIndex: number
  documentId: number
  documentTitle: string
  documentDescription: string | null
  docType: DocumentType | null
  branchId: number | null
  similarity: string
}

export interface KnowledgeBaseStats {
  totalDocuments: number
  totalChunks: number
  totalTokens: number
  uniqueTypes: number
  branchesWithDocs: number
}

export interface KnowledgeBaseListParams {
  page?: number
  limit?: number
  search?: string
  docType?: DocumentType
  branchId?: number
  isActive?: boolean
}

export interface KnowledgeBaseCreateInput {
  title: string
  description?: string
  content: string
  docType?: DocumentType
  branchId?: number
  metadata?: Record<string, unknown>
}

export interface KnowledgeBaseUpdateInput {
  title?: string
  description?: string
  content?: string
  docType?: DocumentType
  branchId?: number | null
  metadata?: Record<string, unknown>
  isActive?: boolean
}

export interface KnowledgeBaseSearchParams {
  query: string
  limit?: number
  branchId?: number
  docType?: DocumentType
  threshold?: number
}

export interface KnowledgeBaseStatsResponse {
  success: boolean
  data: KnowledgeBaseStats
}

export interface KnowledgeBaseListResponse {
  success: boolean
  data: KnowledgeBaseDocument[]
  pagination: {
    page: number
    limit: number
    total: number
    totalPages: number
  }
}

export interface KnowledgeBaseDocumentResponse {
  success: boolean
  data: KnowledgeBaseDocumentWithChunks
}

export interface KnowledgeBaseSearchResponse {
  success: boolean
  query: string
  data: KnowledgeBaseSearchResult[]
}

export interface KnowledgeBaseContextResponse {
  success: boolean
  context: string
  chunks: { title: string; similarity: string }[]
  totalTokens: number
}

// ============================================================================
// Chat Session Types
// ============================================================================
export interface ChatSession {
  id: number
  title: string | null
  branch: {
    id: number
    code: string
    name: string
    shortName: string
  } | null
  modelId: string | null
  messageCount: number | null
  totalTokens: number | null
  createdAt: string
  updatedAt: string
}

export interface ChatMessage {
  id: number
  role: 'user' | 'assistant' | 'system'
  content: string
  toolCalls: unknown[] | null
  toolResults: unknown[] | null
  artifacts: unknown[] | null
  reasoning: string | null
  reasoningDuration: number | null
  promptTokens: number | null
  completionTokens: number | null
  createdAt: string
}

export interface ChatSessionsListResponse {
  sessions: ChatSession[]
  total: number
  limit: number
  offset: number
}

export interface ChatSessionDetailResponse {
  session: ChatSession
  messages: ChatMessage[]
}

// ============================================================================
// Chat Session API
// ============================================================================

/**
 * List all chat sessions (most recent first)
 * Filters by userId if provided
 */
export async function listChatSessions(params?: {
  limit?: number
  offset?: number
  userId?: string
}): Promise<ChatSessionsListResponse> {
  const searchParams = new URLSearchParams()
  if (params?.limit) searchParams.set('limit', params.limit.toString())
  if (params?.offset) searchParams.set('offset', params.offset.toString())
  if (params?.userId) searchParams.set('userId', params.userId)

  const query = searchParams.toString()
  return apiFetch<ChatSessionsListResponse>(`/chat/sessions${query ? `?${query}` : ''}`)
}

/**
 * Get a specific chat session with all messages
 */
export async function getChatSession(sessionId: number): Promise<ChatSessionDetailResponse> {
  return apiFetch<ChatSessionDetailResponse>(`/chat/sessions/${sessionId}`)
}

/**
 * Delete a chat session
 */
export async function deleteChatSession(
  sessionId: number
): Promise<{ success: boolean; deletedId: number }> {
  return apiFetch<{ success: boolean; deletedId: number }>(`/chat/sessions/${sessionId}`, {
    method: 'DELETE',
  })
}

/**
 * Create a new chat session
 */
export async function createChatSession(params?: {
  title?: string
  branchId?: number
  modelId?: string
}): Promise<{ success: boolean; session: ChatSession }> {
  return apiFetch<{ success: boolean; session: ChatSession }>('/chat/sessions', {
    method: 'POST',
    body: JSON.stringify(params || {}),
  })
}

/**
 * Update a chat session (e.g., rename)
 */
export async function updateChatSession(
  sessionId: number,
  params: { title?: string }
): Promise<{ success: boolean; session: ChatSession }> {
  return apiFetch<{ success: boolean; session: ChatSession }>(`/chat/sessions/${sessionId}`, {
    method: 'PATCH',
    body: JSON.stringify(params),
  })
}

// ============================================================================
// WhatsApp Conversation Types
// ============================================================================

export type WAConversationStatus = 'open' | 'archived' | 'closed'
export type WAMessageDirection = 'incoming' | 'outgoing'
export type WAMessageStatus = 'pending' | 'sent' | 'delivered' | 'read' | 'failed'
export type WAMessageType =
  | 'text'
  | 'image'
  | 'document'
  | 'audio'
  | 'video'
  | 'sticker'
  | 'location'
  | 'contacts'
  | 'template'
  | 'interactive'
  | 'reaction'
  | 'unknown'

export interface WAWindowInfo {
  isOpen: boolean
  expiresAt: string | null
  remaining: number | null
  formatted: string | null
}

export interface WAContact {
  id: number
  waPhoneNumber: string
  displayName: string | null
  profilePictureUrl: string | null
  optInStatus: string | null
  lastIncomingAt: string | null
  lastMessageAt: string | null
  totalMessages: number | null
}

export interface WAConversationPatient {
  id: number
  name: string
  pno: string
  gender: string | null
  phone: string | null
  nric: string | null
  totalVisits: number | null
  totalSpent: string | null
  lastVisitDate: string | null
  registrationDate: string | null
}

export interface WAConversationBranch {
  id: number
  name: string
  shortName: string | null
}

export interface WAConversationUser {
  id: number
  name: string
  email?: string
}

export interface WAConversation {
  id: number
  contactId: number
  branchId: number | null
  status: WAConversationStatus
  unreadCount: number
  assignedUserId: number | null
  aiAutoReply: boolean
  tags: string[] | null
  lastMessageAt: string | null
  lastMessagePreview: string | null
  createdAt: string
  updatedAt?: string
  contact: WAContact
  patient: WAConversationPatient | null
  branch: WAConversationBranch | null
  assignedUser: WAConversationUser | null
  window: WAWindowInfo
}

export interface WAMessageSender {
  id: number
  name: string
}

export interface WAMessage {
  id: number
  waMessageId: string
  conversationId: number
  direction: WAMessageDirection
  status: WAMessageStatus
  messageType: WAMessageType
  textBody: string | null
  messageContent: string | null
  mediaId: string | null
  mediaUrl: string | null
  mediaMimeType: string | null
  mediaFileName: string | null
  templateName: string | null
  templateVariables: string | null
  isAiGenerated: boolean | null
  createdAt: string
  sentAt: string | null
  deliveredAt: string | null
  readAt: string | null
  failedAt: string | null
  failureReason: string | null
  sender: WAMessageSender | null
}

export interface WAConversationsListParams {
  cursor?: string
  limit?: number
  branchId?: number
  status?: WAConversationStatus | 'all'
  assignedUserId?: number
  unreadOnly?: boolean
  hasReplies?: boolean
  search?: string
  sortBy?: 'lastMessageAt' | 'createdAt' | 'unreadCount'
  sortOrder?: 'asc' | 'desc'
}

export interface WAConversationsListResponse {
  data: WAConversation[]
  pagination: {
    hasMore: boolean
    nextCursor: string | null
    limit: number
  }
}

export interface WAConversationStats {
  total: number
  open: number
  archived: number
  closed: number
  unread: number
  unassigned: number
}

export interface WAMessagesListParams {
  cursor?: string
  limit?: number
  direction?: 'older' | 'newer'
}

export interface WAMessagesListResponse {
  data: WAMessage[]
  pagination: {
    hasMore: boolean
    nextCursor: string | null
    limit: number
    direction: 'older' | 'newer'
  }
}

export interface WASendMessageInput {
  type: 'text' | 'template' | 'image' | 'document'
  text?: string
  templateName?: string
  languageCode?: string
  bodyVariables?: string[]
  headerVariable?: string
  mediaUrl?: string
  mediaId?: string
  mediaFileName?: string
  caption?: string
}

export interface WAMediaUploadResponse {
  mediaId: string
  mimeType: string
  fileName: string
  fileSize: number
}

export interface WAUpdateConversationInput {
  status?: WAConversationStatus
  assignedUserId?: number | null
  tags?: string[]
  aiAutoReply?: boolean
}

// ============================================================================
// WhatsApp Template Types
// ============================================================================

export type WATemplateStatus = 'pending' | 'approved' | 'rejected' | 'paused'
export type WATemplateCategory = 'MARKETING' | 'UTILITY' | 'AUTHENTICATION'

export interface WATemplate {
  id: number
  metaTemplateId: string | null
  name: string
  language: string
  category: WATemplateCategory
  status: WATemplateStatus
  headerType: string | null
  headerContent: string | null
  bodyContent: string
  footerContent: string | null
  buttonContent: unknown[] | null
  variableMapping: Record<string, string> | null
  totalSent: number
  totalDelivered: number
  totalRead: number
  lastApprovedAt: string | null
  createdAt: string
  updatedAt: string
}

export interface WATemplateDetail extends WATemplate {
  variableCount: number
}

export interface WATemplatesListParams {
  page?: number
  limit?: number
  status?: WATemplateStatus | 'all'
  category?: WATemplateCategory | 'all'
  language?: string
  search?: string
  sortBy?: 'name' | 'category' | 'status' | 'createdAt' | 'totalSent'
  sortOrder?: 'asc' | 'desc'
}

export interface WATemplatesListResponse {
  data: WATemplate[]
  pagination: {
    page: number
    limit: number
    total: number
    totalPages: number
  }
}

export interface WATemplateStats {
  total: number
  byStatus: {
    approved: number
    pending: number
    rejected: number
    paused: number
  }
  byCategory: {
    marketing: number
    utility: number
    authentication: number
  }
}

export interface WATemplateSyncResponse {
  success: boolean
  message: string
  synced: number
  created: number
  updated: number
}

export interface WATemplateUpdateInput {
  variableMapping?: Record<string, string>
}

export interface WATemplateCreateInput {
  name: string
  category: WATemplateCategory
  language?: string
  headerType?: 'none' | 'text' | 'image'
  headerText?: string
  bodyContent: string
  footerContent?: string
  buttons?: Array<{
    type: 'QUICK_REPLY' | 'URL' | 'PHONE_NUMBER'
    text: string
    url?: string
    phoneNumber?: string
  }>
  exampleValues?: string[]
}

export interface WATemplatePreviewResponse {
  template: {
    id: number
    name: string
    category: WATemplateCategory
    status: WATemplateStatus
  }
  preview: {
    header: string | null
    body: string
    footer: string | null
    buttons: unknown[] | null
  }
  variables: {
    mapping: Record<string, string>
    patientData: Record<string, string>
    unresolved: string[]
  }
}

// ============================================================================
// WhatsApp Campaign Types
// ============================================================================

export type WACampaignStatus = 'draft' | 'scheduled' | 'in_progress' | 'completed' | 'cancelled'

export interface WAAudienceFilter {
  branchIds?: number[]
  inactiveDays?: number
  maxInactiveDays?: number
  minTotalSpent?: number
  maxTotalSpent?: number
  gender?: 'Male' | 'Female'
  ageMin?: number
  ageMax?: number
  visitedFrom?: string
  visitedTo?: string
  birthdayMonth?: number
}

export interface WACampaign {
  id: number
  name: string
  description: string | null
  templateId: number
  templateName: string | null
  audienceId: number | null
  audienceType: string
  audienceFilter: WAAudienceFilter | null
  status: WACampaignStatus
  totalRecipients: number
  sentCount: number
  deliveredCount: number
  readCount: number
  failedCount: number
  mediaId: string | null
  mediaUrl: string | null
  mediaMimeType: string | null
  mediaFileName: string | null
  scheduledAt: string | null
  startedAt: string | null
  completedAt: string | null
  createdAt: string
  updatedAt: string
}

export interface WACampaignDetail extends WACampaign {
  templateStatus: WATemplateStatus | null
  templateCategory: WATemplateCategory | null
  templateHeaderType: string | null
  repliedCount: number
  createdByUserId: number | null
}

export interface WACampaignsListParams {
  page?: number
  limit?: number
  status?: WACampaignStatus | 'all'
  search?: string
  sortBy?: 'name' | 'createdAt' | 'status' | 'totalRecipients'
  sortOrder?: 'asc' | 'desc'
}

export interface WACampaignsListResponse {
  data: WACampaign[]
  pagination: {
    page: number
    limit: number
    total: number
    totalPages: number
  }
}

export interface WACampaignStats {
  total: number
  draft: number
  scheduled: number
  inProgress: number
  completed: number
  cancelled: number
}

export interface WACampaignCreateInput {
  name: string
  description?: string
  templateId: number
  audienceId?: number
  audienceType?: string
  audienceFilter?: WAAudienceFilter
  scheduledAt?: string
}

export interface WACampaignUpdateInput {
  name?: string
  description?: string
  templateId?: number
  audienceId?: number | null
  audienceFilter?: WAAudienceFilter
}

export interface WAAudienceContact {
  contactId: number
  waPhoneNumber: string
  displayName: string | null
  patientId: number
  patientName: string
  patientPno: string
  branchId: number
  branchName: string
  totalSpent: string | null
  lastVisitDate: string | null
}

export interface WAAudiencePreviewResponse {
  campaignId?: number
  filter: WAAudienceFilter
  totalCount: number
  sampleContacts: WAAudienceContact[]
}

export interface WACampaignMessage {
  id: number
  contactId: number
  waPhoneNumber: string
  displayName: string | null
  patientName: string | null
  patientPno: string | null
  branchName: string | null
  status: WAMessageStatus
  templateVariables: string[] | null
  sentAt: string | null
  deliveredAt: string | null
  readAt: string | null
  failedAt: string | null
  failureReason: string | null
}

export interface WACampaignMessagesParams {
  page?: number
  limit?: number
  status?: WAMessageStatus | 'all'
}

export interface WACampaignMessagesResponse {
  data: WACampaignMessage[]
  pagination: {
    page: number
    limit: number
    total: number
    totalPages: number
  }
}

// ============================================================================
// WhatsApp Audience Types
// ============================================================================

export type WAAudienceType = 'dynamic' | 'static' | 'manual'

export interface WAAudience {
  id: number
  name: string
  description: string | null
  type: WAAudienceType
  filters: WAAudienceFilter | null
  contactIds: number[] | null
  estimatedCount: number
  lastEvaluatedAt: string | null
  createdByUserId: number | null
  createdAt: string
  updatedAt: string
}

export interface WAAudiencesListParams {
  page?: number
  limit?: number
  search?: string
  type?: WAAudienceType | 'all'
  sortBy?: 'name' | 'createdAt' | 'estimatedCount' | 'type'
  sortOrder?: 'asc' | 'desc'
}

export interface WAAudiencesListResponse {
  data: WAAudience[]
  pagination: {
    page: number
    limit: number
    total: number
    totalPages: number
  }
}

export interface WAAudienceStats {
  total: number
  dynamic: number
  static: number
  manual: number
}

export interface WAAudienceCreateInput {
  name: string
  description?: string
  type: WAAudienceType
  filters?: WAAudienceFilter
  contactIds?: number[]
}

export interface WAAudienceUpdateInput {
  name?: string
  description?: string
  filters?: WAAudienceFilter
  contactIds?: number[]
}

export interface WAAudiencePreview {
  audienceId: number
  type: WAAudienceType
  totalCount: number
  sampleContacts: WAAudienceContact[]
}

export interface WAAudienceContactSearchResult {
  contactId: number
  waPhoneNumber: string
  displayName: string | null
  patientId: number
  patientName: string
  patientPno: string
  branchId: number
  branchName: string
}

// ============================================================================
// WhatsApp Flow Responses Types
// ============================================================================

export type WALeadScore = 'hot' | 'warm' | 'cold' | 'nurture_bf' | 'review'
export type WALeadStatus =
  | 'new'
  | 'assigned'
  | 'contacted'
  | 'booked'
  | 'attended'
  | 'converted'
  | 'no_show'
  | 'lost'

export interface WAFlowResponsesListParams {
  page?: number
  limit?: number
  search?: string
  score?: WALeadScore | 'all'
  status?: WALeadStatus | 'all'
  branchId?: number
  campaignId?: number
  assignedUserId?: number
  unassigned?: boolean
  sortBy?: 'createdAt' | 'leadScore' | 'status'
  sortOrder?: 'asc' | 'desc'
}

export interface WAFlowResponseRow {
  id: number
  createdAt: string | null
  leadScore: WALeadScore
  status: WALeadStatus
  usia: string | null
  breastfeeding: string | null
  concerns: string[] | null
  budget: string | null
  timing: string | null
  branchCode: string | null
  branchId: number | null
  campaignId: number | null
  assignedUserId: number | null
  contactedAt: string | null
  bookedAt: string | null
  contactId: number | null
  conversationId: number | null
  phone: string | null
  contactDisplayName: string | null
  patientId: number | null
  patientName: string | null
  patientDisplayName: string | null
  patientPno: string | null
  branchName: string | null
  assignedUserName: string | null
}

export interface WAFlowResponsesListResponse {
  data: WAFlowResponseRow[]
  pagination: {
    page: number
    limit: number
    total: number
    totalPages: number
  }
}

export interface WAFlowResponseStats {
  total: number
  byScore: {
    hot: number
    warm: number
    cold: number
    nurture_bf: number
    review: number
  }
  byStatus: {
    new: number
    booked: number
    converted: number
    lost: number
  }
}

export interface WAFlowResponseDetail {
  id: number
  createdAt: string | null
  updatedAt: string | null
  leadScore: WALeadScore
  status: WALeadStatus
  usia: string | null
  breastfeeding: string | null
  concerns: string[] | null
  budget: string | null
  timing: string | null
  branchCode: string | null
  branchId: number | null
  campaignId: number | null
  assignedUserId: number | null
  assignedAt: string | null
  contactedAt: string | null
  bookedAt: string | null
  consultationAt: string | null
  convertedAt: string | null
  lostReason: string | null
  notes: string | null
  flowToken: string | null
  flowName: string | null
  responseData: string
  responseDataParsed: Record<string, unknown> | null
  waMessageId: string
  conversationId: number | null
  contact: {
    id: number
    waPhoneNumber: string
    displayName: string | null
    patientId: number | null
  } | null
  patient: {
    id: number
    name: string
    displayName: string | null
    pno: string
    phone: string | null
    dateOfBirth: string | null
    age: number | null
  } | null
  branch: {
    id: number
    shortName: string | null
    name: string
    code: string
  } | null
  campaign: {
    id: number
    name: string
  } | null
  assignedUser: {
    id: number
    name: string
    role: string
  } | null
}

export interface WAFlowResponseStatusInput {
  status: WALeadStatus
  lostReason?: string
  consultationAt?: string
}

export interface WAFlowCloser {
  id: number
  name: string
  role: string
}

// ============================================================================
// WhatsApp Opt-Out (Do Not Blast) Types
// ============================================================================

export interface WAOptOutSearchResult {
  patientId: number | null
  patientName: string | null
  pno: string | null
  nric: string | null
  branchId: number | null
  branchCode: string | null
  patientPhone: string | null
  contactId: number | null
  waPhoneNumber: string | null
  displayName: string | null
  optInStatus: 'pending' | 'opted_in' | 'opted_out' | null
  optOutDate: string | null
}

export interface WAOptOutSearchResponse {
  data: WAOptOutSearchResult[]
  normalizedPhone: string | null
}

export interface WAOptOutListItem {
  contactId: number
  waPhoneNumber: string
  displayName: string | null
  optOutDate: string | null
  patientId: number | null
  patientName: string | null
  pno: string | null
  branchCode: string | null
  reason: string | null
}

export interface WAOptOutInput {
  contactId?: number
  patientId?: number
  phoneNumber?: string
  reason: string
  notes?: string
}

// ============================================================================
// WhatsApp API
// ============================================================================

export const waApi = {
  optout: {
    search: (q: string) =>
      apiFetch<WAOptOutSearchResponse>(`/wa/contacts/optout/search?q=${encodeURIComponent(q)}`),

    list: () => apiFetch<{ data: WAOptOutListItem[]; total: number }>('/wa/contacts/optout/list'),

    optOut: (data: WAOptOutInput) =>
      apiFetch<{ success: boolean }>('/wa/contacts/optout', {
        method: 'POST',
        body: JSON.stringify(data),
      }),

    restore: (contactId: number, notes?: string) =>
      apiFetch<{ success: boolean }>('/wa/contacts/optout/restore', {
        method: 'POST',
        body: JSON.stringify({ contactId, notes }),
      }),
  },

  conversations: {
    list: (params?: WAConversationsListParams) => {
      const searchParams = new URLSearchParams()
      if (params?.cursor) searchParams.set('cursor', params.cursor)
      if (params?.limit) searchParams.set('limit', params.limit.toString())
      if (params?.branchId) searchParams.set('branchId', params.branchId.toString())
      if (params?.status) searchParams.set('status', params.status)
      if (params?.assignedUserId)
        searchParams.set('assignedUserId', params.assignedUserId.toString())
      if (params?.unreadOnly) searchParams.set('unreadOnly', params.unreadOnly.toString())
      if (params?.hasReplies) searchParams.set('hasReplies', params.hasReplies.toString())
      if (params?.search) searchParams.set('search', params.search)
      if (params?.sortBy) searchParams.set('sortBy', params.sortBy)
      if (params?.sortOrder) searchParams.set('sortOrder', params.sortOrder)
      const query = searchParams.toString()
      return apiFetch<WAConversationsListResponse>(`/wa/conversations${query ? `?${query}` : ''}`)
    },

    stats: () => apiFetch<WAConversationStats>('/wa/conversations/stats'),

    get: (id: number) => apiFetch<WAConversation>(`/wa/conversations/${id}`),

    update: (id: number, data: WAUpdateConversationInput) =>
      apiFetch<WAConversation>(`/wa/conversations/${id}`, {
        method: 'PATCH',
        body: JSON.stringify(data),
      }),

    messages: (id: number, params?: WAMessagesListParams) => {
      const searchParams = new URLSearchParams()
      if (params?.cursor) searchParams.set('cursor', params.cursor)
      if (params?.limit) searchParams.set('limit', params.limit.toString())
      if (params?.direction) searchParams.set('direction', params.direction)
      const query = searchParams.toString()
      return apiFetch<WAMessagesListResponse>(
        `/wa/conversations/${id}/messages${query ? `?${query}` : ''}`
      )
    },

    sendMessage: (id: number, data: WASendMessageInput) =>
      apiFetch<{ success: boolean; message: WAMessage; waMessageId: string }>(
        `/wa/conversations/${id}/messages`,
        {
          method: 'POST',
          body: JSON.stringify(data),
        }
      ),

    markAsRead: (id: number) =>
      apiFetch<{ success: boolean; conversationId: number }>(`/wa/conversations/${id}/read`, {
        method: 'POST',
      }),

    markAsUnread: (id: number) =>
      apiFetch<{ success: boolean; conversationId: number }>(`/wa/conversations/${id}/unread`, {
        method: 'POST',
      }),

    uploadMedia: async (file: File): Promise<WAMediaUploadResponse> => {
      const formData = new FormData()
      formData.append('file', file)

      const response = await fetch(`${API_BASE}/wa/conversations/media/upload`, {
        method: 'POST',
        credentials: 'include',
        body: formData,
      })

      if (!response.ok) {
        const err = await response.json().catch(() => ({ error: 'Upload failed' }))
        throw new Error(err.error || 'Upload failed')
      }

      return response.json()
    },
  },

  templates: {
    list: (params?: WATemplatesListParams) => {
      const searchParams = new URLSearchParams()
      if (params?.page) searchParams.set('page', params.page.toString())
      if (params?.limit) searchParams.set('limit', params.limit.toString())
      if (params?.status) searchParams.set('status', params.status)
      if (params?.category) searchParams.set('category', params.category)
      if (params?.language) searchParams.set('language', params.language)
      if (params?.search) searchParams.set('search', params.search)
      if (params?.sortBy) searchParams.set('sortBy', params.sortBy)
      if (params?.sortOrder) searchParams.set('sortOrder', params.sortOrder)
      const query = searchParams.toString()
      return apiFetch<WATemplatesListResponse>(`/wa/templates${query ? `?${query}` : ''}`)
    },

    stats: () => apiFetch<WATemplateStats>('/wa/templates/stats'),

    get: (id: number) => apiFetch<WATemplateDetail>(`/wa/templates/${id}`),

    create: (data: WATemplateCreateInput) =>
      apiFetch<WATemplate>('/wa/templates', {
        method: 'POST',
        body: JSON.stringify(data),
      }),

    sync: () =>
      apiFetch<WATemplateSyncResponse>('/wa/templates/sync', {
        method: 'POST',
      }),

    update: (id: number, data: WATemplateUpdateInput) =>
      apiFetch<WATemplate>(`/wa/templates/${id}`, {
        method: 'PATCH',
        body: JSON.stringify(data),
      }),

    delete: (id: number) =>
      apiFetch<{ success: boolean; message: string; deletedId: number }>(`/wa/templates/${id}`, {
        method: 'DELETE',
      }),

    preview: (id: number, patientId?: number) => {
      const query = patientId ? `?patientId=${patientId}` : ''
      return apiFetch<WATemplatePreviewResponse>(`/wa/templates/${id}/preview${query}`)
    },
  },

  audiences: {
    list: (params?: WAAudiencesListParams) => {
      const searchParams = new URLSearchParams()
      if (params?.page) searchParams.set('page', params.page.toString())
      if (params?.limit) searchParams.set('limit', params.limit.toString())
      if (params?.search) searchParams.set('search', params.search)
      if (params?.type) searchParams.set('type', params.type)
      if (params?.sortBy) searchParams.set('sortBy', params.sortBy)
      if (params?.sortOrder) searchParams.set('sortOrder', params.sortOrder)
      const query = searchParams.toString()
      return apiFetch<WAAudiencesListResponse>(`/wa/audiences${query ? `?${query}` : ''}`)
    },

    stats: () => apiFetch<WAAudienceStats>('/wa/audiences/stats'),

    get: (id: number) => apiFetch<WAAudience>(`/wa/audiences/${id}`),

    create: (data: WAAudienceCreateInput) =>
      apiFetch<WAAudience>('/wa/audiences', {
        method: 'POST',
        body: JSON.stringify(data),
      }),

    update: (id: number, data: WAAudienceUpdateInput) =>
      apiFetch<WAAudience>(`/wa/audiences/${id}`, {
        method: 'PUT',
        body: JSON.stringify(data),
      }),

    delete: (id: number) =>
      apiFetch<{ success: boolean; message: string; deletedId: number }>(`/wa/audiences/${id}`, {
        method: 'DELETE',
      }),

    preview: (id: number) =>
      apiFetch<WAAudiencePreview>(`/wa/audiences/${id}/preview`, {
        method: 'POST',
      }),

    snapshot: (id: number) =>
      apiFetch<{ success: boolean; message: string; audience: WAAudience }>(
        `/wa/audiences/${id}/snapshot`,
        {
          method: 'POST',
        }
      ),

    previewFilter: (filter: WAAudienceFilter) =>
      apiFetch<WAAudiencePreviewResponse>('/wa/audiences/preview-filter', {
        method: 'POST',
        body: JSON.stringify(filter),
      }),

    searchContacts: (search: string) =>
      apiFetch<{ data: WAAudienceContactSearchResult[] }>(
        `/wa/audiences/contacts/search?search=${encodeURIComponent(search)}`
      ),

    branches: () =>
      apiFetch<{ data: { id: number; name: string | null; code: string }[] }>(
        '/wa/audiences/branches'
      ),
  },

  campaigns: {
    list: (params?: WACampaignsListParams) => {
      const searchParams = new URLSearchParams()
      if (params?.page) searchParams.set('page', params.page.toString())
      if (params?.limit) searchParams.set('limit', params.limit.toString())
      if (params?.status) searchParams.set('status', params.status)
      if (params?.search) searchParams.set('search', params.search)
      if (params?.sortBy) searchParams.set('sortBy', params.sortBy)
      if (params?.sortOrder) searchParams.set('sortOrder', params.sortOrder)
      const query = searchParams.toString()
      return apiFetch<WACampaignsListResponse>(`/wa/campaigns${query ? `?${query}` : ''}`)
    },

    stats: () => apiFetch<WACampaignStats>('/wa/campaigns/stats'),

    get: (id: number) => apiFetch<WACampaignDetail>(`/wa/campaigns/${id}`),

    create: (data: WACampaignCreateInput) =>
      apiFetch<WACampaign>('/wa/campaigns', {
        method: 'POST',
        body: JSON.stringify(data),
      }),

    update: (id: number, data: WACampaignUpdateInput) =>
      apiFetch<WACampaign>(`/wa/campaigns/${id}`, {
        method: 'PATCH',
        body: JSON.stringify(data),
      }),

    delete: (id: number) =>
      apiFetch<{ success: boolean; message: string; deletedId: number }>(`/wa/campaigns/${id}`, {
        method: 'DELETE',
      }),

    previewAudience: (id: number) =>
      apiFetch<WAAudiencePreviewResponse>(`/wa/campaigns/${id}/audience/preview`, {
        method: 'POST',
      }),

    previewFilter: (filter: WAAudienceFilter) =>
      apiFetch<WAAudiencePreviewResponse>('/wa/campaigns/preview-filter', {
        method: 'POST',
        body: JSON.stringify(filter),
      }),

    execute: (id: number, data?: { scheduledAt?: string }) =>
      apiFetch<{ success: boolean; message: string; campaignId: number; scheduledAt?: string }>(
        `/wa/campaigns/${id}/execute`,
        {
          method: 'POST',
          body: JSON.stringify(data || {}),
        }
      ),

    cancel: (id: number) =>
      apiFetch<{ success: boolean; message: string; campaignId: number }>(
        `/wa/campaigns/${id}/cancel`,
        {
          method: 'POST',
        }
      ),

    messages: (id: number, params?: WACampaignMessagesParams) => {
      const searchParams = new URLSearchParams()
      if (params?.page) searchParams.set('page', params.page.toString())
      if (params?.limit) searchParams.set('limit', params.limit.toString())
      if (params?.status) searchParams.set('status', params.status)
      const query = searchParams.toString()
      return apiFetch<WACampaignMessagesResponse>(
        `/wa/campaigns/${id}/messages${query ? `?${query}` : ''}`
      )
    },

    uploadMedia: async (id: number, file: File) => {
      const formData = new FormData()
      formData.append('file', file)

      const res = await fetch(`${API_BASE}/wa/campaigns/${id}/media`, {
        method: 'POST',
        credentials: 'include',
        body: formData,
        // Do NOT set Content-Type — browser sets multipart boundary automatically
      })

      if (!res.ok) {
        const error = await res.json().catch(() => ({ error: 'Upload failed' }))
        throw new Error(error.error || `HTTP ${res.status}`)
      }

      return res.json() as Promise<{
        success: boolean
        mediaId: string
        fileName: string
        mimeType: string
        size: number
      }>
    },

    deleteMedia: (id: number) =>
      apiFetch<{ success: boolean; message: string }>(`/wa/campaigns/${id}/media`, {
        method: 'DELETE',
      }),

    getExportUrl: (id: number) => `${API_BASE}/wa/campaigns/${id}/export`,
  },

  flowResponses: {
    list: (params?: WAFlowResponsesListParams) => {
      const searchParams = new URLSearchParams()
      if (params?.page) searchParams.set('page', params.page.toString())
      if (params?.limit) searchParams.set('limit', params.limit.toString())
      if (params?.search) searchParams.set('search', params.search)
      if (params?.score && params.score !== 'all') searchParams.set('score', params.score)
      if (params?.status && params.status !== 'all') searchParams.set('status', params.status)
      if (params?.branchId) searchParams.set('branchId', params.branchId.toString())
      if (params?.campaignId) searchParams.set('campaignId', params.campaignId.toString())
      if (params?.assignedUserId)
        searchParams.set('assignedUserId', params.assignedUserId.toString())
      if (params?.unassigned) searchParams.set('unassigned', 'true')
      if (params?.sortBy) searchParams.set('sortBy', params.sortBy)
      if (params?.sortOrder) searchParams.set('sortOrder', params.sortOrder)
      const query = searchParams.toString()
      return apiFetch<WAFlowResponsesListResponse>(`/wa/flow-responses${query ? `?${query}` : ''}`)
    },

    stats: () => apiFetch<WAFlowResponseStats>('/wa/flow-responses/stats'),

    get: (id: number) => apiFetch<WAFlowResponseDetail>(`/wa/flow-responses/${id}`),

    assign: (id: number, assignedUserId: number | null) =>
      apiFetch<WAFlowResponseRow>(`/wa/flow-responses/${id}/assign`, {
        method: 'PATCH',
        body: JSON.stringify({ assignedUserId }),
      }),

    updateStatus: (id: number, data: WAFlowResponseStatusInput) =>
      apiFetch<WAFlowResponseRow>(`/wa/flow-responses/${id}/status`, {
        method: 'PATCH',
        body: JSON.stringify(data),
      }),

    updateNotes: (id: number, notes: string) =>
      apiFetch<WAFlowResponseRow>(`/wa/flow-responses/${id}/notes`, {
        method: 'PATCH',
        body: JSON.stringify({ notes }),
      }),

    closers: () => apiFetch<{ data: WAFlowCloser[] }>('/wa/flow-responses/closers/list'),

    getExportUrl: () => `${API_BASE}/wa/flow-responses/export`,
  },
}

// ============================================================================
// Redemption Tracker Types
// ============================================================================

export type RedemptionRiskTier = 'critical' | 'warning' | 'active' | 'churn'

export interface RedemptionListParams {
  branchId?: number
  riskTier?: 'all' | RedemptionRiskTier
  search?: string
  treatmentMasterId?: number
  minOutstanding?: number
  page?: number
  pageSize?: number
  sortBy?: 'outstanding' | 'lastVisit' | 'name'
  sortOrder?: 'asc' | 'desc'
}

export interface RedemptionPatientRow {
  patientId: number
  name: string
  pno: string
  phone: string | null
  branchId: number
  branchName: string | null
  outstanding: number
  lastVisit: string | null
  daysSinceVisit: number
  riskTier: RedemptionRiskTier
}

export interface RedemptionSummary {
  totalOutstanding: number
  critical: number
  warning: number
  active: number
  churn: number
  pendingSessions: number
  avgDaysSinceVisit: number
}

export interface RedemptionListResponse {
  summary: RedemptionSummary
  patients: RedemptionPatientRow[]
  pagination: { page: number; pageSize: number; total: number }
}

export interface RedemptionBreakdownEntry {
  treatmentId: number
  masterName: string
  promoCode: string | null
  purchased: number
  consumed: number
  outstanding: number
  firstPurchase: string | null
  firstRedemption: string | null
  lastRedemption: string | null
  redemptionCount: number
  pricePaid: number | null
  avgCadenceDays: number | null
  nextDueEstimate: string | null
}

export interface RedemptionTimelineEntry {
  invoiceNo: string
  invoiceDate: string
  grandTotal: number
  rawTreatmentName: string
  quantity: number
  totalPrice: number
  isFoc: boolean
  promoCode: string | null
  masterName: string | null
  treatmentType: string | null
}

export interface RedemptionDetailResponse {
  patient: {
    id: number
    name: string
    pno: string
    phone: string | null
    nric: string | null
    branchId: number
    branchName: string | null
  }
  breakdown: RedemptionBreakdownEntry[]
  timeline: RedemptionTimelineEntry[]
}

// ============================================================================
// Follow-ups Types (docs/follow-ups-spec.md)
// ============================================================================

export type FollowUpQueue =
  | 'package_done'
  | 'last_session'
  | 'stale_package'
  | 'consult_no_purchase'
  | 'no_show'
  | 'lapsed_regular'

export type FollowUpStatus =
  | 'pending'
  | 'contacted'
  | 'replied'
  | 'booked'
  | 'converted'
  | 'no_answer'
  | 'callback'
  | 'thinking'
  | 'not_interested'
  | 'expired'
  | 'blacklisted'

/** Status filter accepted by GET /follow-ups — grouped buckets + `all` + any raw status. */
export type FollowUpStatusFilter = 'open' | 'contacted' | 'booked' | 'all' | FollowUpStatus

export type FollowUpEventType =
  | 'queued'
  | 'assigned'
  | 'wa_sent'
  | 'wa_opened'
  | 'called'
  | 'replied'
  | 'booked'
  | 'repurchased'
  | 'no_answer'
  | 'callback'
  | 'thinking'
  | 'not_interested'
  | 'note'
  | 'expired'
  | 'blacklisted'

export type FollowUpSource = 'auto_wa' | 'auto_avixo' | 'manual' | 'system'

/** WA-redirect tracking buttons in the drawer (wa.me deep link flow — CA's own phone).
 * Kept separate from `FollowUpManualOutcome` — these don't count as one of the 4
 * decision outcomes in history/report filters. */
export type FollowUpManualTracking = 'wa_opened' | 'wa_sent' | 'called' | 'replied'

/** Decision outcomes a CA can record. `blacklisted` also flags the patient
 * (patients.blacklisted_at) so they leave every queue and campaign audience. */
export type FollowUpManualOutcome =
  | 'no_answer'
  | 'callback'
  | 'thinking'
  | 'not_interested'
  | 'blacklisted'

/** Everything `POST /:id/events` accepts. */
export type FollowUpAddEventType = FollowUpManualTracking | FollowUpManualOutcome | 'note'

export interface FollowUpTriggerRef {
  packageRedemptionIds?: number[]
  packageName?: string
  doctor?: string | null
  boughtQty?: number
  usedQty?: number
  balanceQty?: number
  balanceValue?: number
  lastVisit?: string | null
  invoiceId?: number
  invoiceNo?: string
  appointmentId?: number
}

export interface FollowUpPatientRef {
  id: number
  name: string
  pno: string
  phone: string | null
  gender?: string | null
  /** Set when the clinic blacklisted this patient. */
  blacklistedAt: string | null
}

export interface FollowUpWaRef {
  contactId: number
  conversationId: number
  optInStatus: string
  lastIncomingAt: string | null
  /** True when the 24h free-text window is open — the Nurin inbox can send
   * free text; otherwise the drawer falls back to the wa.me redirect flow. */
  waWindowOpen: boolean
}

export interface FollowUpLastEvent {
  type: FollowUpEventType
  source: FollowUpSource
  note: string | null
  createdAt: string
}

/** One row in the "This week" table / list response. */
export interface FollowUpRow {
  id: number
  branchId: number
  patientId: number
  queue: FollowUpQueue
  status: FollowUpStatus
  assignedTo: number | null
  assigneeName: string | null
  weekStart: string
  value: number
  reason: string
  triggerRef: FollowUpTriggerRef
  lastVisit: string | null
  /** Consult date (consult queue), missed appointment date (no-show) or last
   * visit (package / lapsed queues). The page's period filter matches on this. */
  eventDate: string | null
  createdAt: string
  updatedAt: string
  resolvedAt: string | null
  patient: FollowUpPatientRef
  /** https://wa.me/<digits> deep link for the CA's own phone, or null when the
   * patient's phone can't be normalized. */
  waLink: string | null
  wa: FollowUpWaRef | null
  lastEvent: FollowUpLastEvent | null
}

export interface FollowUpEvent {
  id: number
  followUpId: number
  type: FollowUpEventType
  source: FollowUpSource
  userId: number | null
  userName?: string | null
  note: string | null
  refType: 'wa_message' | 'appointment' | 'invoice' | 'template' | null
  refId: string | null
  createdAt: string
}

export interface FollowUpVisit {
  date: string
  kind: 'invoice' | 'appointment'
  label: string
  amount: number | null
  doctor: string | null
}

export interface FollowUpPackage {
  name: string
  bought: number
  used: number
  balance: number
  balanceValue: number
  doctor: string | null
}

export interface FollowUpDetail extends FollowUpRow {
  events: FollowUpEvent[]
  visits: FollowUpVisit[]
  packages: FollowUpPackage[]
}

/** GET /follow-ups/summary — one entry per queue for the tiles row. */
export interface FollowUpQueueSummary {
  total: number
  pending: number
  contacted: number
  booked: number
  value: number
}

export interface FollowUpSummary {
  queues: Record<FollowUpQueue, FollowUpQueueSummary>
  updatedAt: string
}

export type FollowUpHistoryMode = 'outcome' | 'queued'
export type FollowUpHistorySource = 'manual' | 'system' | 'all'

export interface FollowUpHistoryTotals {
  followUps: number
  open: number
  expired: number
  contacted: number
  bookedAfterContact: number
  bookedAny: number
  repurchased: number
  recoveredValue: number
}

/** One card in "Staff activity" — grouped by who logged the action. */
export interface FollowUpHistoryStaff {
  userId: number
  name: string
  role: string
  branches: string | null
  assigned: number
  contacted: number
  bookedAfterContact: number
  repurchased: number
  recoveredValue: number
  openUntouched: number
}

export interface FollowUpHistoryByQueue {
  queue: FollowUpQueue
  followUps: number
  contacted: number
  bookedAfterContact: number
  bookedAny: number
  repurchased: number
  recoveredValue: number
}

export interface FollowUpHistoryActions {
  total: number
  withOutcome: number
  byType: { type: FollowUpEventType; count: number }[]
}

export interface FollowUpHistoryEventRow extends FollowUpEvent {
  patientId: number
  patientName: string
  queue: FollowUpQueue
  followUpStatus: FollowUpStatus
  bookedAt: string | null
  bookedAppointmentAt: string | null
  repurchasedAt: string | null
  repurchasedAmount: number | null
}

export interface FollowUpHistory {
  month: string
  mode: FollowUpHistoryMode
  totals: FollowUpHistoryTotals
  byStaff: FollowUpHistoryStaff[]
  byQueue: FollowUpHistoryByQueue[]
  actions: FollowUpHistoryActions
  events: {
    data: FollowUpHistoryEventRow[]
    total: number
    counts: { manual: number; system: number; all: number }
  }
}

export interface FollowUpEpisode extends FollowUpRow {
  events: FollowUpEvent[]
}

export interface FollowUpByPatientResponse {
  episodes: FollowUpEpisode[]
}

export interface FollowUpSummaryParams {
  branchId?: number
  status?: FollowUpStatusFilter
  assignedTo?: number
  /** Event-date range, YYYY-MM-DD inclusive (max 6 months). */
  from?: string
  to?: string
}

export interface FollowUpsListParams {
  branchId?: number
  queue?: FollowUpQueue
  status?: FollowUpStatusFilter
  assignedTo?: number
  /** Event-date range, YYYY-MM-DD inclusive (max 6 months). */
  from?: string
  to?: string
  search?: string
  sort?: 'priority' | 'value' | 'lastVisit' | 'eventDate' | 'createdAt'
  page?: number
  pageSize?: number
}

export interface FollowUpHistoryParams {
  branchId?: number
  /** YYYY-MM */
  month: string
  /** `outcome` (default): count by when things happened. `queued`: cohort view. */
  mode?: FollowUpHistoryMode
  staffId?: number
  queue?: FollowUpQueue
  /** Event log segment. */
  source?: FollowUpHistorySource
  /** Hide "Entered queue" / "Assigned" rows (default true). */
  hideNoise?: boolean
  search?: string
  page?: number
  pageSize?: number
}

export interface FollowUpAddEventInput {
  type: FollowUpAddEventType
  note?: string
}

export type FollowUpSummaryResponse = FollowUpSummary

export interface FollowUpsListResponse {
  data: FollowUpRow[]
  total: number
  page: number
  pageSize: number
}

export interface FollowUpAssigneesResponse {
  data: { id: number; name: string }[]
}

export type FollowUpDetailResponse = FollowUpDetail

export interface FollowUpScriptResponse {
  templateKey: string
  text: string
}

export interface FollowUpGenerateResponse {
  reconciled: {
    wa_sent: number
    replied: number
    booked: number
    repurchased: number
    expired: number
  }
  queued: Record<FollowUpQueue, { inserted: number; eligible: number }>
  assigned: number
}
